"use client";

import { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  category: string;
  prefix: string;
  value: number;
  suffix: string;
  display: string;
  descriptor: string;
};
const stats: Stat[] = [{
  category: "REVENUE GROWTH",
  prefix: "+",
  value: 45,
  suffix: "%",
  display: "+45%",
  descriptor: "Average increase in RevPAR for our partner properties"
}, {
  category: "OTA LISTINGS",
  prefix: "",
  value: 1200,
  suffix: "+",
  display: "1200+",
  descriptor: "Optimized property listings across global travel platforms"
}, {
  category: "DIRECT BOOKINGS",
  prefix: "",
  value: 35,
  suffix: "%",
  display: "35%",
  descriptor: "Average shift from OTA dependency to direct revenue"
}, {
  category: "TECH AUDITS",
  prefix: "",
  value: 85,
  suffix: "",
  display: "85",
  descriptor: "Hospitality tech stacks audited and streamlined for efficiency"
}];
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function formatNumber(value: number, target: Stat): string {
  if (target.suffix === "B+") {
    return `${target.prefix}${value.toFixed(1)}${target.suffix}`;
  }
  if (target.suffix === "B") {
    return `${target.prefix}${Math.floor(value)}${target.suffix}`;
  }
  if (target.value >= 1000) {
    return `${target.prefix}${Math.floor(value).toLocaleString("en-US")}${target.suffix}`;
  }
  return `${target.prefix}${Math.floor(value)}${target.suffix}`;
}
function AnimatedCounter({
  stat,
  start
}: {
  stat: Stat;
  start: boolean;
}) {
  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const duration = 1200;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      setCurrent(stat.value * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDone(true);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, stat.value]);
  return <div className="flex flex-col items-start">
      <div className="font-default font-bold text-primary leading-none text-5xl sm:text-6xl lg:text-[88px] tracking-tight tabular-nums">
        {start ? formatNumber(current, stat) : `${stat.prefix}0${stat.suffix.replace(/[0-9.]/g, "")}`}
      </div>
      <div className="mt-4 h-px bg-secondary transition-[width] ease-out" style={{
      width: done ? "48px" : "0px",
      transitionDuration: "600ms"
    }} aria-hidden="true" />
    </div>;
}
export default function Performancemetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  return <section data-section-id="4555" id="performancemetrics" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true">
        <div className="mx-auto h-full max-w-7xl px-6 lg:px-10">
          <div className="grid h-full grid-cols-4 lg:grid-cols-12">
            {Array.from({
            length: 12
          }).map((_, i) => <div data-index={i} key={i} className="border-l border-border h-full hidden lg:block last:border-r" />)}
            {Array.from({
            length: 4
          }).map((_, i) => <div data-index={i} key={`m-${i}`} className="border-l border-border h-full lg:hidden last:border-r" />)}
          </div>
        </div>
      </div>

      <div ref={ref} className="relative mx-auto max-w-7xl px-6 lg:px-10">
        {}
        <p className="font-default text-xs uppercase tracking-[0.2em] text-secondary font-semibold">
          02 / Proven Results
        </p>

        {}
        <h2 className="font-default font-bold mt-6 max-w-3xl text-3xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-primary">
          Measurable impact for your hotel revenue.
        </h2>

        {}
        <div className="mt-10 h-px w-20 bg-secondary" aria-hidden="true" />

        {}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12">
          {stats.map((stat, i) => <div data-index={i} key={stat.category} className="relative flex flex-col gap-6 sm:px-6 lg:px-8 first:pl-0 lg:first:pl-0">
              {}
              {i > 0 && <Separator orientation="vertical" className="hidden lg:block absolute left-0 top-0 bg-border" />}
              {}
              {i > 0 && i !== 2 && <Separator orientation="vertical" className="hidden sm:block lg:hidden absolute left-0 top-0 bg-border" style={{
            display: i % 2 === 1 ? undefined : "none"
          }} />}

              {}
              <p className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">
                {stat.category}
              </p>

              {}
              <AnimatedCounter stat={stat} start={isVisible} />

              {}
              <p className="font-default text-sm leading-relaxed text-muted-foreground max-w-[28ch]">
                {stat.descriptor}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}